import { Component, inject, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { DetectedAccident } from '../../../models/accidents-and-incidents-response.model';
import { ClaimInsuranceTab } from '../../../enums/claim-insurance-tab.enum';
import { areUploadsComplete, toAttachmentRequests } from '../../../helpers/incident-attachment-upload.helper';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { InsuranceService } from '../../../services/insurance.service';
import { ClaimDocumentUpload, InsuranceClaimData } from './claim-insurance.model';
import { DocumentsTabComponent } from './components/documents-tab/documents-tab.component';
import { InsuranceClaimTabComponent } from './components/insurance-claim-tab/insurance-claim-tab.component';

@Component({
  selector: 'app-claim-insurance-modal',
  standalone: true,
  imports: [
    CommonModule,
    DocumentsTabComponent,
    InsuranceClaimTabComponent,
    TranslocoDirective,
    TranslocoPipe
  ],
  templateUrl: './claim-insurance-modal.component.html',
  styleUrl: './claim-insurance-modal.component.scss'
})
export class ClaimInsuranceModalComponent implements OnDestroy {
  @Input() title: string = '';
  @Input() accident: DetectedAccident | null = null;
  @Input() assetId: number | null = null;
  // Pass in either accidentId (from Detected Accident) or incidentId (from Incident Report), never both
  @Input() accidentId: number | null = null;
  @Input() incidentId: number | null = null;
  @Input() categoryId: number = 0; // Leave as 0 for accident claims, the backend defaults their category to Collision

  private activeModal: NgbActiveModal = inject(NgbActiveModal);
  private insuranceService = inject(InsuranceService);
  private errorHandlerService = inject(ErrorHandlerService);
  private toastr: ToastrService = inject(ToastrService);
  private readonly transloco: TranslocoService = inject(TranslocoService);

  readonly claimInsuranceTab = ClaimInsuranceTab;

  activeTab: ClaimInsuranceTab = ClaimInsuranceTab.Documents;
  uploadedDocuments: ClaimDocumentUpload[] = [];
  isSubmitting: boolean = false;
  private cancelSubject = new Subject<void>();

  get areDocumentsReady(): boolean {
    return areUploadsComplete(this.uploadedDocuments);
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  setActiveTab(tab: ClaimInsuranceTab): void {
    this.activeTab = tab;
  }

  onDocumentsChange(documents: ClaimDocumentUpload[]): void {
    this.uploadedDocuments = documents;
  }

  onDocumentsSave(documents: ClaimDocumentUpload[]): void {
    this.uploadedDocuments = documents;
    this.activeTab = ClaimInsuranceTab.Insurance;
  }

  async onInsuranceClaimSave(claimData: InsuranceClaimData): Promise<void> {
    if (!this.assetId || !claimData.severity || !this.areDocumentsReady) {
      return;
    }

    this.isSubmitting = true;

    try {
      await this.insuranceService.createInsuranceClaim({
        assetId: this.assetId,
        incidentId: this.incidentId,
        accidentId: this.accidentId,
        insuranceName: claimData.insuranceCompany,
        policyNumber: claimData.policyNumber,
        description: claimData.incidentDescription || null,
        categoryId: this.categoryId,
        severityId: claimData.severity,
        attachments: toAttachmentRequests(this.uploadedDocuments)
      }, this.cancelSubject);

      this.toastr.success(this.transloco.translate('accidents.modals.claimInsurance.claimFiled'));
      this.activeModal.close(true);
    } catch (error) {
      this.isSubmitting = false;
      this.errorHandlerService.handleError(error, { showToast: true });
    }
  }

  cancel(): void {
    this.activeModal.dismiss();
  }
}